import {
  propertyLinkCategoryOptions,
  propertyLinkExampleValue,
} from '../../../utils/property-link-template.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { PropertyLinkValueMappingsProps } from '../../../types/property-links.types';

/** Edit equivalences with their variable; applying the variable commits all complete rows together. */
export function PropertyLinkValueMappings({
  parameter,
  rows,
  disabled,
  onChange,
}: PropertyLinkValueMappingsProps) {
  const { t } = useI18n();
  const categories = propertyLinkCategoryOptions(parameter, t);
  return (
    <div className="space-y-3">
      <Text size="small" weight="medium">
        {t('propertyLinks.customizeValues')}
      </Text>
      <Text size="small" color="secondary">
        {t('propertyLinks.mappingHelp')}
      </Text>
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid items-end gap-2 rounded-lg border border-secondary-200 p-3 sm:grid-cols-[1fr_1fr_auto]"
        >
          <div className="min-w-0 space-y-1">
            <Text size="tiny">{t('propertyLinks.mappingFrom')}</Text>
            {categories.length ? (
              <Select
                aria-label={t('propertyLinks.mappingFrom')}
                value={row.from}
                disabled={disabled}
                options={[
                  { value: '', content: t('propertyLinks.chooseValue') },
                  ...categories,
                ]}
                onChange={(event) =>
                  onChange(
                    rows.map((item, i) =>
                      i === index ? { ...item, from: event.target.value } : item
                    )
                  )
                }
              />
            ) : (
              <TextInput
                aria-label={t('propertyLinks.mappingFrom')}
                placeholder={propertyLinkExampleValue(parameter)}
                value={row.from}
                disabled={disabled}
                maxLength={2048}
                onChange={(event) =>
                  onChange(
                    rows.map((item, i) =>
                      i === index ? { ...item, from: event.target.value } : item
                    )
                  )
                }
              />
            )}
          </div>
          <div className="min-w-0 space-y-1">
            <Text size="tiny">{t('propertyLinks.mappingTo')}</Text>
            <TextInput
              aria-label={t('propertyLinks.mappingTo')}
              placeholder={t('propertyLinks.mappingToPlaceholder')}
              value={row.to}
              disabled={disabled}
              maxLength={2048}
              onChange={(event) =>
                onChange(
                  rows.map((item, i) =>
                    i === index ? { ...item, to: event.target.value } : item
                  )
                )
              }
            />
          </div>
          <Button
            type="button"
            size="sm"
            appearance="ghost"
            variant="neutral"
            disabled={disabled}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            {t('propertyLinks.removeMapping')}
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        appearance="outline"
        disabled={disabled || rows.length >= 100}
        onClick={() => onChange([...rows, { from: '', to: '' }])}
      >
        {t('propertyLinks.addMapping')}
      </Button>
    </div>
  );
}
